'use client'

import { useState } from 'react'
import { Plus, Crown } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/use-toast'
import { trpc } from '@/lib/api/trpc'
import { useOffersList, type OfferItem } from '@/lib/api/offers'
import { ClubOfferCardView } from './ClubOfferCardView'
import { ClubOfferModal } from './ClubOfferModal'

const ClubOfferCard = ({
    offer,
    onClick,
    onDelete,
}: {
    offer: OfferItem
    onClick: () => void
    onDelete: () => void
}) => {
    const { i18n } = useTranslation()
    const title = offer.titleTrans?.[i18n.language] || offer.titleTrans?.en || ''
    const summary = offer.summaries?.[0]
    const currency = summary?.currency || 'USD'
    const regular = summary?.totalAmount || 0
    const club = summary?.totalAdjustedAmount ?? regular
    const imageUrl = offer.offerLineItems?.[0]?.price?.variant?.imageUrl ?? null

    return (
        <ClubOfferCardView
            title={title}
            imageUrl={imageUrl}
            regularMinor={regular}
            clubMinor={club}
            currency={currency}
            expiryDate={offer.expiryDate}
            productCount={offer.offerLineItems?.length}
            onClick={onClick}
            onDelete={onDelete}
        />
    )
}

const CreateTile = ({ onClick, label }: { onClick: () => void; label: string }) => (
    <button
        type="button"
        onClick={onClick}
        className="flex min-h-[280px] w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-muted-foreground/30 bg-card/40 text-muted-foreground transition hover:border-amber-400 hover:text-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-400"
    >
        <Plus className="h-8 w-8" />
        <span className="font-medium">{label}</span>
    </button>
)

export const ClubOffersTab = ({ storeId }: { storeId: string }) => {
    const { t } = useTranslation('club')
    const { toast } = useToast()
    const utils = trpc.useUtils()

    const [modalOpen, setModalOpen] = useState(false)
    const [editingOfferId, setEditingOfferId] = useState<string | null>(null)
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)

    const listInput = { storeId, type: 'club' as const }
    const { data, isLoading, error, refetch } = useOffersList(listInput)
    const offers = data?.offers || []

    // Optimistic delete: drop the card from cache immediately, roll back on error.
    const deleteOffer = trpc.offers.delete.useMutation({
        onMutate: async ({ offerIds }) => {
            await utils.offers.list.cancel(listInput)
            const previous = utils.offers.list.getData(listInput)
            utils.offers.list.setData(listInput, (old) =>
                old ? { ...old, offers: old.offers.filter((o) => !offerIds.includes(o.id)) } : old,
            )
            return { previous }
        },
        onError: (err, _vars, ctx) => {
            if (ctx?.previous) utils.offers.list.setData(listInput, ctx.previous)
            toast({
                title: t('offers.toasts.deleteError'),
                description: err.message,
                variant: 'destructive',
            })
        },
        onSettled: () => {
            utils.offers.list.invalidate(listInput)
        },
    })

    const openCreate = () => {
        setEditingOfferId(null)
        setModalOpen(true)
    }
    const openEdit = (id: string) => {
        setEditingOfferId(id)
        setModalOpen(true)
    }

    const confirmDelete = () => {
        if (!confirmDeleteId) return
        deleteOffer.mutate({ offerIds: [confirmDeleteId], storeId })
        setConfirmDeleteId(null)
    }

    return (
        <div className="space-y-4">
            <div>
                <h2 className="text-lg font-semibold sm:text-xl">{t('offers.title')}</h2>
                <p className="text-muted-foreground text-sm">{t('offers.description')}</p>
            </div>

            {error && (
                <div className="rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700">
                    {error instanceof Error ? error.message : t('offers.loadError')}
                </div>
            )}

            <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(240px,1fr))]">
                <CreateTile onClick={openCreate} label={t('offers.create')} />

                {isLoading
                    ? Array.from({ length: 3 }).map((_, i) => (
                          <Skeleton
                              key={i}
                              className="flex min-h-[280px] w-full items-center justify-center rounded-xl"
                          >
                              <Crown className="h-10 w-10 text-gray-300" />
                          </Skeleton>
                      ))
                    : offers.map((offer) => (
                          <ClubOfferCard
                              key={offer.id}
                              offer={offer}
                              onClick={() => openEdit(offer.id)}
                              onDelete={() => setConfirmDeleteId(offer.id)}
                          />
                      ))}
            </div>

            <ClubOfferModal
                storeId={storeId}
                open={modalOpen}
                offerId={editingOfferId}
                onOpenChange={setModalOpen}
                onSaved={refetch}
            />

            <AlertDialog open={!!confirmDeleteId} onOpenChange={(o) => !o && setConfirmDeleteId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('offers.delete.title')}</AlertDialogTitle>
                        <AlertDialogDescription>{t('offers.delete.description')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('offers.delete.cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {t('offers.delete.confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}
